import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { EditPaymentSlideOver } from '@/components/payments/EditPaymentSlideOver.js';
import { VoidPaymentModal } from '@/components/payments/VoidPaymentModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import {
  listPaymentsByTicket,
  type PaymentRecord,
} from '@/lib/api/payments.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import {
  isPaymentVoided,
  paymentCardType,
  paymentLastFour,
  paymentMethodEmoji,
  paymentMethodLabel,
  paymentTransactionId,
  staffShortName,
  toPaymentRecord,
} from '@/lib/payment-display.js';
import { resolveTicketBalance } from '@/lib/ticket-balance.js';
import { cn } from '@/lib/utils.js';

export function TicketPaymentsPanel({
  ticketId,
  ticketNumber,
  customerName,
  paidTotal,
  balanceDue,
  ticketTotal,
  initialPayments,
  onAddPayment,
  onChanged,
  compact = false,
  className,
}: {
  ticketId: string;
  ticketNumber?: string;
  customerName?: string;
  paidTotal?: string | number | null;
  balanceDue?: string | number | null;
  ticketTotal?: string | number | null;
  initialPayments?: Array<Partial<PaymentRecord> & { id: string; amount: string; method: string; createdAt: string; status?: string }>;
  onAddPayment?: () => void;
  onChanged?: () => void;
  compact?: boolean;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const { requireOverride, modalProps } = useManagerOverride();
  const [editPayment, setEditPayment] = useState<PaymentRecord | null>(null);
  const [voidTarget, setVoidTarget] = useState<PaymentRecord | null>(null);
  const [voidAuthorizedBy, setVoidAuthorizedBy] = useState<string | null>(null);

  const paymentsQuery = useQuery({
    queryKey: ['payments', 'ticket', ticketId],
    queryFn: () => listPaymentsByTicket(ticketId),
    enabled: Boolean(ticketId),
    placeholderData: initialPayments?.map((p) => toPaymentRecord(p)),
  });

  const payments = paymentsQuery.data ?? initialPayments?.map((p) => toPaymentRecord(p)) ?? [];

  const activePaid = useMemo(
    () =>
      payments
        .filter((p) => !isPaymentVoided(p))
        .reduce((sum, p) => sum + Number(p.amount), 0),
    [payments],
  );

  const balance = resolveTicketBalance({
    total: ticketTotal,
    paidTotal: paidTotal ?? activePaid,
    balanceDue,
  });
  const displayPaid = balance.paidTotal;
  const displayBalance = balance.balanceDue;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['payments', 'ticket', ticketId] });
    onChanged?.();
  }

  async function handleVoidClick(payment: PaymentRecord) {
    const result = await requireOverride({
      action: 'payment.void',
      title: 'Manager Approval to Void Payment',
      description: `Voiding ${formatCurrency(payment.amount)} ${paymentMethodLabel(payment.method).toLowerCase()} payment${
        ticketNumber ? ` on ${ticketNumber}` : ''
      }`,
      context: {
        ticketId,
        amount: Number(payment.amount),
      },
    });
    if (!result.approved) return;
    setVoidAuthorizedBy(result.authorizedBy.id);
    setVoidTarget(payment);
  }

  return (
    <div className={cn(compact ? '' : 'space-y-2', className)}>
      <div className="flex items-center justify-between">
        <p className={cn('font-bold uppercase tracking-wide text-slate-500', compact ? 'text-xs' : 'text-sm')}>
          Payments
        </p>
        {onAddPayment ? (
          <button
            type="button"
            onClick={onAddPayment}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
        ) : null}
      </div>

      {paymentsQuery.isLoading && payments.length === 0 ? (
        <p className="text-sm text-slate-500">Loading payments…</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-slate-500">No payments recorded.</p>
      ) : (
        <div className="space-y-2">
          {payments.map((payment) => {
            const voided = isPaymentVoided(payment);
            const cardType = paymentCardType(payment);
            const lastFour = paymentLastFour(payment);
            const transId = paymentTransactionId(payment);
            const recordedBy = payment.recordedBy
              ? staffShortName(payment.recordedBy)
              : '';
            const voidedBy = payment.voidedBy ? staffShortName(payment.voidedBy) : '';

            return (
              <div
                key={payment.id}
                className={cn(
                  'rounded-lg px-3 py-2 text-xs',
                  voided ? 'bg-slate-100 text-slate-400' : 'bg-slate-50 text-slate-700',
                )}
              >
                <div
                  className={cn(
                    'flex justify-between font-semibold capitalize',
                    voided && 'line-through',
                  )}
                >
                  <span>
                    {paymentMethodEmoji(payment.method)}{' '}
                    {payment.method === 'card' && (cardType || lastFour)
                      ? [
                          cardType
                            ? cardType.charAt(0).toUpperCase() + cardType.slice(1)
                            : 'Card',
                          lastFour ? `•••• ${lastFour}` : null,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : paymentMethodLabel(payment.method)}
                  </span>
                  <span>{formatCurrency(payment.amount)}</span>
                </div>

                {voided ? (
                  <div className="mt-1 space-y-0.5">
                    <p className="font-semibold text-slate-500">
                      VOIDED
                      {payment.voidedAt ? ` — ${formatDateTime(payment.voidedAt)}` : ''}
                    </p>
                    {voidedBy ? <p>Voided by: {voidedBy}</p> : null}
                    {payment.voidReason ? <p>Reason: “{payment.voidReason}”</p> : null}
                  </div>
                ) : (
                  <>
                    <p className="text-slate-500">{formatDateTime(payment.paidAt ?? payment.createdAt)}</p>
                    {recordedBy ? <p className="text-slate-500">Recorded by: {recordedBy}</p> : null}
                    {transId ? <p>Trans ID: {transId}</p> : null}
                    {payment.editedAt ? (
                      <p className="text-amber-700">
                        Edited {formatDateTime(payment.editedAt)}
                        {payment.editNotes ? ` — ${payment.editNotes}` : ''}
                      </p>
                    ) : null}
                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-white"
                        onClick={() => setEditPayment(payment)}
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                        onClick={() => void handleVoidClick(payment)}
                      >
                        <Trash2 className="h-3 w-3" />
                        Void
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-semibold">
        <span className="text-slate-600">Paid: {formatCurrency(displayPaid)}</span>
        <span
          className={
            balance.amountToCollect > 0
              ? 'text-amber-700'
              : balance.isOverpaid
                ? 'text-amber-800'
                : 'text-green-700'
          }
        >
          {balance.isOverpaid
            ? `Credit: ${formatCurrency(balance.creditOwed)}`
            : balance.amountToCollect <= 0
              ? 'Balance: Paid in full'
              : `Balance: ${formatCurrency(balance.amountToCollect)}`}
        </span>
      </div>

      <EditPaymentSlideOver
        open={Boolean(editPayment)}
        payment={editPayment}
        ticketTotal={ticketTotal}
        onClose={() => setEditPayment(null)}
        onSuccess={refresh}
      />

      <VoidPaymentModal
        open={Boolean(voidTarget && voidAuthorizedBy)}
        payment={voidTarget}
        ticketNumber={ticketNumber}
        customerName={customerName}
        managerOverrideAuthorizedBy={voidAuthorizedBy ?? ''}
        defaultTicketAction={
          ticketNumber?.startsWith('SALE-') ? 'keep_closed' : 'reopen'
        }
        onClose={() => {
          setVoidTarget(null);
          setVoidAuthorizedBy(null);
        }}
        onSuccess={() => refresh()}
      />

      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </div>
  );
}
